if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.variantIdInput.disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');
                this.submitButtonText = this.submitButton.querySelector('span');

                if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

                this.hideErrors = this.dataset.hideErrors === 'true';

                // Add validation for personalization fields on input changes
                // But don't validate on initial load
                const personalizationFields = document.querySelectorAll('input[name^="properties"][required]');
                personalizationFields.forEach(field => {
                    // Only validate after user has interacted with the field
                    field.addEventListener('input', () => {
                        if (field.dataset.userInteracted) {
                            this.validatePersonalizationField(field);
                        }
                    });

                    field.addEventListener('blur', () => {
                        // Mark as interacted once user leaves the field
                        field.dataset.userInteracted = 'true';
                        if (field.dataset.userInteracted) {
                            this.validatePersonalizationField(field);
                        }
                    });
                });
            }

            validatePersonalizationField(field) {
                if (!field.value.trim()) {
                    field.classList.add('error');

                    // Show validation message if not already present
                    let errorMessage = field.parentNode.querySelector('.field__error');
                    if (!errorMessage) {
                        errorMessage = document.createElement('div');
                        errorMessage.className = 'field__error';
                        errorMessage.textContent = 'This field is required';
                        field.parentNode.appendChild(errorMessage);
                    }
                    return false;
                } else {
                    field.classList.remove('error');
                    const errorMessage = field.parentNode.querySelector('.field__error');
                    if (errorMessage) errorMessage.remove();
                    return true;
                }
            }

            async onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

                // Validate personalization fields if they exist
                const personalizationFields = document.querySelectorAll('input[name^="properties"][required]');
                let isValid = true;

                personalizationFields.forEach(field => {
                    // Mark all fields as interacted during form submission
                    field.dataset.userInteracted = 'true';
                    const fieldValid = this.validatePersonalizationField(field);
                    if (!fieldValid) {
                        isValid = false;
                        field.focus();
                    }
                });

                if (!isValid) {
                    return false;
                }

                this.handleErrorMessage();

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                this.querySelector('.loading__spinner').classList.remove('hidden');

                // Check if we need to add engraving product
                const personalizationInput = document.querySelector('[data-personalization-input]');
                const productMetaPersonalizationRequired = this.form.closest('product-info').dataset.productId;
                const isPersonalizationRequired = document.querySelector('input[name^="properties"][required]');
                const hasPersonalizationText = personalizationInput && personalizationInput.value.trim();
                const shouldAddEngraving = hasPersonalizationText && !isPersonalizationRequired;

                if (shouldAddEngraving) {
                    // Add both products as a bundle
                    await this.addProductBundle(personalizationInput.value);
                } else {
                    // Add single product as normal
                    await this.addSingleProduct();
                }
            }

            async addProductBundle(personalizationText) {
                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                const bundleId = Date.now(); // Unique ID to link products

                // Add personalization properties to main product
                formData.append('properties[Personalize your bottle]', personalizationText);
                formData.append('properties[_bundle_id]', bundleId);
                formData.append('properties[_bundle_main]', 'true');

                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }

                try {
                    // Add main product
                    const mainResponse = await fetch(`${routes.cart_add_url}`, {
                        ...config,
                        body: formData
                    });
                    const mainResult = await mainResponse.json();

                    if (mainResult.status) {
                        throw new Error(mainResult.description || 'Failed to add main product');
                    }

                    // Get engraving product info dynamically
                    const engravingVariantId = await this.getEngravingVariantId();
                    if (!engravingVariantId) {
                        throw new Error('Engraving product not found');
                    }

                    // Add engraving product
                    const engravingFormData = new FormData();
                    engravingFormData.append('id', engravingVariantId);
                    engravingFormData.append('quantity', '1');
                    engravingFormData.append('properties[_bundle_id]', bundleId);
                    engravingFormData.append('properties[_bundle_addon]', 'true');
                    engravingFormData.append('properties[_personalization_text]', personalizationText);

                    if (this.cart) {
                        engravingFormData.append(
                            'sections',
                            this.cart.getSectionsToRender().map((section) => section.id)
                        );
                        engravingFormData.append('sections_url', window.location.pathname);
                    }

                    const engravingResponse = await fetch(`${routes.cart_add_url}`, {
                        ...config,
                        body: engravingFormData
                    });
                    const engravingResult = await engravingResponse.json();

                    if (engravingResult.status) {
                        throw new Error(engravingResult.description || 'Failed to add engraving product');
                    }

                    // Update global cart state with the final result
                    await this.updateCartState(engravingResult);

                    // Handle success
                    this.handleSuccess(engravingResult);

                } catch (error) {
                    this.handleError(error.message);
                } finally {
                    this.submitButton.classList.remove('loading');
                    if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                    if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                    this.querySelector('.loading__spinner').classList.add('hidden');
                }
            }

            async addSingleProduct() {
                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);

                // Manually add personalization properties to form data
                const personalizationFields = document.querySelectorAll('input[name^="properties"][required]');
                personalizationFields.forEach(field => {
                    if (field.value.trim()) {
                        formData.append(field.name, field.value);
                    }
                });

                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButtonText.classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        }

                        this.handleSuccess(response);
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        this.querySelector('.loading__spinner').classList.add('hidden');
                    });
            }

            handleSuccess(response) {
                if (!this.cart) {
                    window.location = window.routes.cart_url;
                    return;
                }

                if (!this.error)
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: response.items ? response.items[0]?.variant_id : null,
                        cartData: response,
                    });
                this.error = false;
                const quickAddModal = this.closest('quick-add-modal');
                if (quickAddModal) {
                    document.body.addEventListener(
                        'modalClosed',
                        () => {
                            setTimeout(() => {
                                this.cart.renderContents(response);
                            });
                        },
                        { once: true }
                    );
                    quickAddModal.hide(true);
                } else {
                    this.cart.renderContents(response);
                }
            }

            handleError(errorMessage) {
                publish(PUB_SUB_EVENTS.cartError, {
                    source: 'product-form',
                    errors: errorMessage,
                    message: errorMessage,
                });
                this.handleErrorMessage(errorMessage);
                this.error = true;
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;
                this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }

            toggleSubmitButton(disable = true, text) {
                if (disable) {
                    this.submitButton.setAttribute('disabled', 'disabled');
                    if (text) this.submitButtonText.textContent = text;
                } else {
                    this.submitButton.removeAttribute('disabled');
                    this.submitButtonText.textContent = window.variantStrings.addToCart;
                }
            }

            async getEngravingVariantId() {
                // Try multiple possible product handles (prioritize 'engraving' since we found it)
                const possibleHandles = [
                    'engraving',
                    'personalization',
                    'bottle-engraving',
                    'custom-engraving',
                    'personalized-engraving'
                ];

                for (const handle of possibleHandles) {
                    try {
                        const response = await fetch(`/products/${handle}.js`);
                        if (response.ok) {
                            const product = await response.json();

                            if (product.variants && product.variants.length > 0) {
                                const variant = product.variants[0]; // Use first variant
                                return variant.id;
                            }
                        }
                    } catch (error) {
                        // Continue to next handle
                    }
                }

                // Try to get variant info directly from cart API
                try {
                    const response = await fetch('/cart.js');
                    if (response.ok) {
                        const cart = await response.json();
                        // Look for existing engraving items in cart to get variant info
                        const engravingItem = cart.items.find(item =>
                            item.properties && item.properties._bundle_addon === 'true'
                        );
                        if (engravingItem) {
                            return engravingItem.variant_id;
                        }
                    }
                } catch (error) {
                    // Continue to fallback
                }

                // Fallback to hardcoded ID if dynamic lookup fails
                return '43123779502191';
            }

            async updateCartState(cartData) {
                try {
                    // Fetch fresh cart data to ensure we have the complete state
                    const cartResponse = await fetch('/cart.js');
                    const freshCartData = await cartResponse.json();

                    // Update global cart state
                    window.cart = freshCartData;

                } catch (error) {
                    // Silent error handling
                }
            }

            get variantIdInput() {
                return this.form.querySelector('[name=id]');
            }
        }
    );
}
